import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { faMagicWandSparkles, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { StudioService } from '../../services/studio/studio.service';
import { AuthService } from '../../services/auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-studio',
  templateUrl: './studio.component.html',
  styleUrl: './studio.component.scss',
  standalone: false,
})
export class StudioComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  activeTab: 'OCR Text' | 'Raw JSON' | 'Formatted' | 'Category Details' = 'OCR Text';
  selectedCategory: any = null;
  categoryControl: FormControl<string> = new FormControl('', { nonNullable: true });
  categoryOptions: any[] = [];
  isLoading: boolean = false;
  selectedFileName: string = '';
  selectedFile: File | null = null;
  pdfUrl: string = '';
  isProcessed: boolean = false;
  page: number = 0;
  isDragOver: boolean = false;
  showFileUpload: boolean = true;
  errorMsg: string = '';
  toasterMsg: string = '';
  showCategoryModal: boolean = false;
  availableCategories: any[] = [];

  faMagicWandSparkles = faMagicWandSparkles;
  faSave = faSave;
  faArrowLeft = faArrowLeft;
  faSpinner = faSpinner;

  constructor(
    private readonly studioService: StudioService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ocrText: string = '';
  rawJson: any = null;
  stracturedData: any[] = [];
  metadata: any = null;
  credits: number | null = null;

  ngOnInit(): void {
    this.loadCategories();
    this.loadCredits();
  }

  loadCredits(): void {
    this.studioService.getUserCredits().subscribe({
      next: (res) => {
        this.credits = res?.data?.credits ?? res?.data?.credit ?? res?.credits ?? res?.credit ?? null;
      },
      error: () => { this.credits = null; }
    });
  }

  loadCategories(): void {
    this.studioService.getStudioMetadata({ page: 0 }).subscribe({
      next: (response) => {
        const items = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        this.categoryOptions = items.map((item: any) => ({
          id: item?._id,
          name: item?.category || item?.name || 'Untitled'
        }));
      },
      error: (error) => {
        console.error('Error loading categories:', error);
      }
    });
  }

  onCategoryChange(categoryId: string): void {
    this.selectedCategory = categoryId;
  }

  onFileSelected(event: any) {
    this.toasterMsg = '';
    const file = event.target.files[0];
    if (file) {
    if (!this.processFile(file)) {
      this.fileInput.nativeElement.value = '';
     return; }
      this.selectedFileName = file.name;
      this.selectedFile = file;
      this.pdfUrl = URL.createObjectURL(file);
      this.isProcessed = false;
      this.ocrText = '';
      this.rawJson = null;
      this.stracturedData = [];
      this.metadata = null;
      this.errorMsg = '';
      console.log('File selected', this.selectedFileName);
    }
  }

  onProcess() {
    if (!this.selectedFile) {
      console.warn('No file selected');
      return;
    }
    this.isLoading = true;
    this.showFileUpload = false;
    this.errorMsg = '';
    this.toasterMsg = '';
    this.studioService.getOcrData(this.selectedFile, this.selectedCategory).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        let extractionData: any = null;
        let errorMessages: string[] = [];
        if (res?.data) {
          if (res.data.result) {
            extractionData = res.data.result;
          }
          else if (res.data.message && !res.data.rawText && !res.data.jsonData) {
            if (res.data.message.toLowerCase().includes('multiple') || res.data.message.toLowerCase().includes('categor')) {
              // Show category selection popup and keep the file
              if (this.categoryOptions && this.categoryOptions.length > 0) {
                this.availableCategories = this.categoryOptions;
                this.showCategoryModal = true;
              }
              return;
            }
            this.errorMsg = res.data.message;
            this.toasterMsg = res.data.message;
            this.isProcessed = false;
            this.showFileUpload = true;
            return;
          }
          else if (Array.isArray(res.data)) {
            for (let item of res.data) {
              if (typeof item === 'string') {
                errorMessages.push(item);
              } else if (typeof item === 'object' && (item.rawText || item.jsonData)) {
                extractionData = item;
              }
            }
          } 
          else if (typeof res.data === 'object' && (res.data.rawText || res.data.jsonData)) {
            extractionData = res.data;
          }
          else if (res.data.resultJson) {
            extractionData = res.data.resultJson;
          }
          else if (res.rawText || res.jsonData) {
            extractionData = res;
          }
        } else if (res.rawText || res.jsonData) {
          extractionData = res;
        }

        if (errorMessages.length > 0) {
          this.errorMsg = errorMessages.join(' ');
          this.toasterMsg = errorMessages.join(' ');
          this.isProcessed = false;
          this.showFileUpload = true;
          return;
        }

        if (extractionData) {
          this.isProcessed = true;
          this.ocrText = this.formatOcrText(extractionData.rawText || 'No text extracted');
          this.rawJson = extractionData.jsonData || {};
          this.stracturedData = this.transformData(extractionData.jsonData || {});
        } else {
          this.isProcessed = false;
          this.showFileUpload = true;
        }
        
        if (res?.data?.result?.usageMetadata) {
          this.metadata = res.data.result.usageMetadata;
        } else if (res?.data?.usageMetadata) {
          this.metadata = res.data.usageMetadata;
        } else if (res?.usageMetadata) {
          this.metadata = res.usageMetadata;
        }        
        if (res?.data?.categoryDetails) {
          if (!this.metadata) {
            this.metadata = {};
          }
          this.metadata.categoryDetails = res.data.categoryDetails;
        }
      },
      error: (err: any) => {
        this.isLoading = false;
        this.isProcessed = false;
        this.showFileUpload = true; 
        const errorMessage = err?.error?.error?.message || err?.error?.message || err?.message || 'Processing failed. Please try again.';
        this.errorMsg = errorMessage;
        this.toasterMsg = errorMessage;
      }
    });
  }

  transformData(data: any): any[] {
    let result: any[] = [];
    let parentKeys = Object.keys(data || {});
    for (let key of parentKeys) {
      let obj: any = {
        key: key.replace(/_/g, ' '),
        value: null,
        childs: [],
        table: []
      };
      let moreObjs: any[] = [];
      let value = data[key];
      let isObj = typeof value === 'object' && !Array.isArray(value);
      let isArray = Array.isArray(value);
      if (isObj) {
        let subKeys = Object.keys(value || {});
        for (let subKey of subKeys) {
          let subValue = value[subKey];
          let type = typeof subValue;
          if (type === 'string') {
            obj.childs.push({
              key: subKey?.replace(/_/g, ' '),
              value: typeof subValue === 'string' ? subValue : JSON.stringify(subValue)
            });
          } else if (Array.isArray(subValue)) {
            let result = this.transformArray(subValue, subKey);
            if (result.type === 'Table') {
              moreObjs.push({
                key: subKey?.replace(/_/g, ' '),
                value: '',
                table: result,
              });
            } else {
              obj.childs.push({
                key: subKey?.replace(/_/g, ' '),
                value: typeof result === 'string' ? result : JSON.stringify(result)
              });
            }
          } else if (type === 'object' && !Array.isArray(subValue)) {
            let temp = this.transformObject(subValue);
            const normal = temp.filter(item => !item.table);
            const withTable = temp.filter(item => item.table);
            moreObjs.push({
              key: subKey?.replace(/_/g, ' '),
              value: '',
              childs: normal
            });
            moreObjs = [...moreObjs, ...withTable];
          } else {
            obj.childs.push({
              key: subKey?.replace(/_/g, ' '),
              value: typeof subValue === 'string' ? subValue : JSON.stringify(subValue)
            });
          }
        }
      } else if (isArray) {
        let result = this.transformArray(value, key);
        if (result.type === 'Table') {
          obj.table = result;
        } else {
          obj.value = result;
        }
      } else {
        obj.value = `${value}`;
      }
      result.push(obj);
      if (moreObjs) {
        result = [...result, ...moreObjs];
      }
    }
    return result;
  }

  transformObject(data: any) {
    if (!data) {
      return [];
    }
    let returnValue: any[] = [];
    for (let key of Object.keys(data || {})) {
      let obj: any = {
        key: key?.replace(/_/g, ' ')
      };
      let value = data[key];
      let isString = typeof value === 'string';
      let isObj = typeof value === 'object' && !Array.isArray(value);
      let isArray = Array.isArray(value);

      if (isString) {
        obj.value = typeof value === 'string' ? value : JSON.stringify(value);
      } else if (isObj) {
        let subKeys = Object.keys(value || {});
        let childArray: any = [];
        for (let subKey of subKeys) {
          let childsObj: any = {
            key: subKey?.replace(/_/g, ' '),
            value: '',
            table: null,
            childs: null
          };
          let subValue = value[subKey];
          let type = typeof subValue;
          if (type === 'string') {
            childsObj.value = typeof subValue === 'string' ? subValue : JSON.stringify(subValue);
          } else if (Array.isArray(subValue)) {
            let result = this.transformArray(subValue, subKey);
            if (result.type === 'Table') {
              childsObj.table = result;
            } else {
              childsObj.value = typeof result === 'string' ? result : JSON.stringify(result);
            }
          } else {
            let result = this.transformObject(subValue);
            childsObj.childs = result;
          }
          childArray.push(childsObj);
        }
        obj.childs = childArray;
      } else if (isArray) {
        let result = this.transformArray(value, key);
        if (result.type === 'Table') {
          obj.table = result;
        } else {
          obj.value = typeof result === 'string' ? result : JSON.stringify(result);
        }
      } else {
        obj.value = typeof value === 'string' ? value : JSON.stringify(value);
      }
      returnValue.push(obj);
    }
    return returnValue;
  }

  transformArray(value: any, key: any) {
    let type = this.getArrayType(value);
    if (type === 'string') {
      return value.join(', ');
    } else if (type === 'object') {
      let allKeys = new Set<string>();
      for (let item of value) {
        Object.keys(item || {}).forEach(k => allKeys.add(k));
      }
      let keys = Array.from(allKeys);
      let obj: any = {
        type: 'Table',
        keys: keys,
        values: [] as any[],
      };
      for (let item of value) {
        let array: (string | null)[] = [];
        for (let key of keys) {
          array.push(typeof item[key] === 'string' ? item[key] : (item[key] !== undefined ? JSON.stringify(item[key]) : ''));
        }
        obj.values.push(array);
      }
      obj.keys = obj.keys.map((key: string) => key.replace(/_/g, ' '));
      return obj;
    } else if (type === 'Mixed') {
      return value.map((item: any) => typeof item === 'string' ? item : JSON.stringify(item)).join(', ');
    }
  }

  getArrayType(arr: any) {
    if (!Array.isArray(arr)) return 'NA';
    if (arr.every(item => typeof item === 'string')) {
      return 'string';
    }
    if (arr.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
      return 'object';
    }
    return 'Mixed';
  }

  onDiscard() {
    this.isProcessed = false;
    this.isLoading = false;
    this.selectedFileName = '';
    this.selectedFile = null;
    this.pdfUrl = '';
    this.ocrText = '';
    this.rawJson = null;
    this.stracturedData = [];
    this.metadata = null;
    this.activeTab = 'OCR Text';
    this.showFileUpload = true;
    this.errorMsg = '';
  }

  closeErrorMsg() {
    this.errorMsg = '';
  }

  closeToaster() {
    this.toasterMsg = '';
  }

  triggerFileInputClick() {
    this.fileInput?.nativeElement?.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!this.processFile(file)) return;
      this.selectedFileName = file.name;
      this.selectedFile = file;
      this.pdfUrl = URL.createObjectURL(file);
      this.isProcessed = false;
      this.ocrText = '';
      this.rawJson = null;
      this.stracturedData = [];
      this.metadata = null;
      this.errorMsg = '';
    }
  }

  setTab(tab: 'OCR Text' | 'Raw JSON' | 'Formatted' | 'Category Details') {
    this.activeTab = tab;
  }

  processFile(file: File): boolean { 
    this.toasterMsg = '';
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.toasterMsg = 'File size should not exceed 10 MB.';
      return false;
    }
    return true;
  }

  formatText(value: string): string {
    if (!value) return '';
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_\-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  formatOcrText(text: string): string {
    if (!text) return '';
    let formatted = text
      .replace(/\s{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      // Trim each line
      .split('\n')
      .map(line => line.trim())
      .join('\n')
      .replace(/[ \t]+$/gm, '')
      .trim();
    return formatted;
  }

  selectCategory(categoryId: string): void {
    this.selectedCategory = categoryId;
    this.categoryControl.setValue(categoryId);
    this.showCategoryModal = false;
  }

  closeCategoryModal(): void {
    this.showCategoryModal = false;
    this.selectedFileName = '';
    this.selectedFile = null;
    this.pdfUrl = '';
    this.fileInput.nativeElement.value = '';
  }

  get metadataEntries(): { key: string, value: any }[] {
    if (!this.metadata) return [];
    return Object.keys(this.metadata)
      .filter(k => k !== 'categoryDetails')
      .map(k => ({ key: k, value: this.metadata[k] }));
  }

  get categoryDetailEntries(): { key: string, value: any }[] {
    if (!this.metadata?.categoryDetails) return [];
    return Object.keys(this.metadata.categoryDetails)
      .filter(k => k !== 'id')
      .map(k => ({ key: k, value: this.metadata.categoryDetails[k] }));
  }

  isSimpleValue(val: any): boolean {
    return typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean';
  }
}